/**
 * Forced updates: Hub compares the build it runs (baked in at build time) with
 * `/release-info.json`, which every deploy rewrites. Pure rules; see ReleaseWatcher.tsx.
 */

/** Off switch: false stops all version checks and forced refreshes (deploy with this changed). */
export const FORCED_UPDATES_ENABLED = true;
/** Checked every minute while Hub is visible, and whenever it comes back to the front. */
export const RELEASE_POLL_MS = 60_000;
/** Seconds the update modal waits before refreshing (time to finish the current step). */
export const UPDATE_COUNTDOWN_SECONDS = 60;
/** A refresh that came back on the old build (CDN still serving it) is not forced again for this long. */
export const RELOAD_RETRY_MS = 120_000;
/** Back after at least this long away: nobody is mid-task, so a newer build loads at once. */
export const LONG_AWAY_MS = 5 * 60_000;

/**
 * Forced refreshes into one build per tab session. Each one that comes back on the old build (a CDN
 * still serving it, or a broken deploy whose release-info.json names a build that never loads)
 * counts; after this many Hub stops forcing that build until the next deploy names another.
 */
export const MAX_RELOAD_ATTEMPTS = 3;

export const RELOAD_GUARD_KEY = 'hub-release-reload';

export const needsUpdate = (
  running: string | null | undefined,
  latest: string | null | undefined,
) => Boolean(running && latest && running !== latest);

/** No one is working there (hidden, or a sign-in page): refresh at once. */
export const refreshesImmediately = (hidden: boolean, pathname: string) =>
  hidden || /^\/(login|forgot-password|reset-password)(\/|$)/.test(pathname);

export type ReloadAttempt = { release: string; at: number; count: number };

/**
 * At most one forced refresh per new build every RELOAD_RETRY_MS and MAX_RELOAD_ATTEMPTS in all,
 * so neither a stale CDN nor a broken deploy can keep refreshing Hub. `force`: Update now, which
 * the operator chose, skips the wait but not the cap.
 */
export const canForceReload = (
  release: string,
  last: ReloadAttempt | null,
  now: number,
  force = false,
) =>
  !last ||
  last.release !== release ||
  (last.count < MAX_RELOAD_ATTEMPTS && (force || now - last.at >= RELOAD_RETRY_MS));

/** The record of a refresh into `release`, counting earlier ones into the same build. */
export const nextReloadAttempt = (
  release: string,
  last: ReloadAttempt | null,
  now: number,
): ReloadAttempt => ({
  release,
  at: now,
  count: last?.release === release ? last.count + 1 : 1,
});

export const parseReloadAttempt = (raw: string | null): ReloadAttempt | null => {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<ReloadAttempt>;
    return typeof value.release === 'string' && typeof value.at === 'number'
      ? {
          release: value.release,
          at: value.at,
          count: typeof value.count === 'number' ? value.count : 1,
        }
      : null;
  } catch {
    return null;
  }
};
